import { useEffect, useState } from 'react';

export function useNetworkStatus(peerConnection = null) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isSlowNetwork, setIsSlowNetwork] = useState(false);
  const [isHighPacketLoss, setIsHighPacketLoss] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Function to check network speed if possible
    const updateConnectionStatus = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection && connection.effectiveType) {
        if (['slow-2g', '2g'].includes(connection.effectiveType)) {
          setIsSlowNetwork(true);
        } else {
          setIsSlowNetwork(false);
        }
      } else {
        // Browser does not support network info — assume normal
        setIsSlowNetwork(false);
      }
    };

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection && connection.addEventListener) {
      connection.addEventListener('change', updateConnectionStatus);
    }
    updateConnectionStatus(); // initial check

    let intervalId = null;

    const checkPacketLoss = async () => {
      if (peerConnection) {
        try {
          const stats = await peerConnection.getStats();
          stats.forEach(report => {
            if ((report.type === 'outbound-rtp' || report.type === 'inbound-rtp') && (report.kind === 'audio' || report.kind === 'video')) {
              const packetsSent = report.packetsSent || 0;
              const packetsReceived = report.packetsReceived || 0;
              const packetsLost = report.packetsLost || 0;

              const totalPackets = packetsSent || packetsReceived || 1; // avoid division by zero
              const packetLossRate = packetsLost / totalPackets;

              if (packetLossRate > 0.05) { // 5% threshold
                setIsHighPacketLoss(true);
              } else {
                setIsHighPacketLoss(false);
              }
            }
          });
        } catch (error) {
          console.error('Error getting WebRTC stats:', error);
        }
      }
    };

    if (peerConnection) {
      intervalId = setInterval(checkPacketLoss, 5000); // every 5 seconds
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection && connection.removeEventListener) {
        connection.removeEventListener('change', updateConnectionStatus);
      }
      if (intervalId) clearInterval(intervalId);
    };
  }, [peerConnection]);

  return { isOffline, isSlowNetwork, isHighPacketLoss };
}